import DualDateRangePicker from 'components/Common/Calendar/DualDateRangePicker'
import VerticalDateRangePicker from 'components/Common/Calendar/VerticalDateRangePicker'
import DropdownSheet from 'LuxKit/Dropdown/Sheet/DropdownSheet'
import FormattedSearchDate from 'components/Search/SearchForm/SearchDateInput/FormattedSearchDate'
import { SearchMenuStates } from 'components/Search/type'
import Group from 'components/utils/Group'
import { DATE_SEARCH_OPTION_IDS } from 'constants/search'
import { GlobalSearchDispatchContext, GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { GlobalSearchStateActions } from 'contexts/GlobalSearch/GlobalSearchState'
import { addDays, addMonths, beginningOfToday, isSameDay } from 'lib/datetime/dateUtils'
import noop from 'lib/function/noop'
import type moment from 'moment'
import type { ComponentProps } from 'react'
import { useCallback, useContext, useMemo, useRef } from 'react'
import { useIntl } from 'react-intl'
import { useScreenSizeOnly } from 'hooks/useScreenSize'

function findMinDate() {
  if (new Date().getHours() === 23) {
    const tomorrow = addDays(new Date(), 1)
    tomorrow.setHours(0, 0, 0, 0)
    return tomorrow
  }
  const today = beginningOfToday()
  return today
}

interface Props extends Pick<ComponentProps<typeof DropdownSheet>, 'anchorRef' | 'triggerRef'> {
  onApply?: () => void
}

function CarHireDatePickerDropdown(props: Props) {
  const { activeMenu, checkinDate: pickUpDate, checkoutDate: returnDate, dateSearchOptionId } = useContext(GlobalSearchStateContext)
  const { anchorRef, triggerRef, onApply = noop } = props
  const intl = useIntl()
  const isMobileScreen = useScreenSizeOnly('mobile')
  const bodyRef = useRef<HTMLDivElement>(null)
  const minDate = useMemo(() => findMinDate(), [])
  const maxDate = useMemo(() => addMonths(new Date(), 18), [])

  const onDatesChange = (dates: {
    startDate: moment.Moment
    endDate: moment.Moment | undefined
  }) => {
    if (dates?.startDate && isSameDay(dates.startDate.toDate(), new Date())) {
      searchDispatch({ type: GlobalSearchStateActions.UNSET_PICKUP_TIME })
    }

    if (dates?.endDate && isSameDay(dates.endDate.toDate(), new Date())) {
      searchDispatch({ type: GlobalSearchStateActions.UNSET_RETURN_TIME })
    }

    searchDispatch({ type: GlobalSearchStateActions.SET_CHECKIN_DATE, date: dates.startDate })
    searchDispatch({ type: GlobalSearchStateActions.SET_CHECKOUT_DATE, date: dates.endDate })
  }

  const clearDates = () => {
    searchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKIN_DATE })
    searchDispatch({ type: GlobalSearchStateActions.UNSET_CHECKOUT_DATE })
    toggleDropdown()
  }

  const searchDispatch = useContext(GlobalSearchDispatchContext)

  const showDropdown = activeMenu === SearchMenuStates.CarHireDatePicker

  const toggleDropdown = useCallback(() => {
    const nextMenu = showDropdown ? SearchMenuStates.Closed : SearchMenuStates.CarHireDatePicker
    searchDispatch({ type: GlobalSearchStateActions.SET_ACTIVE_MENU, menu: nextMenu })
    onApply()
  }, [onApply, searchDispatch, showDropdown])

  const hasDates = !!(pickUpDate && returnDate)
  const disableSearchButton = dateSearchOptionId === DATE_SEARCH_OPTION_IDS.SPECIFIC && !hasDates

  const applyLabel = intl.formatMessage({
    id: 'oS-63txHAPATiOvUy0V_',
    defaultMessage: 'Apply',
    description: 'Button text to apply selected dates in car hire date picker',
  })

  const cancelLabel = intl.formatMessage({
    id: 'F1GUYyBUMgVIPuzm7rEh',
    defaultMessage: 'Cancel',
    description: 'Button text to cancel date selection in car hire date picker',
  })

  const pickUpLabel = intl.formatMessage({
    id: 'fTEN0S1R2zqKHexS9Jt-',
    defaultMessage: 'Pick-up',
    description: 'Label for pick-up date in car hire date range picker',
  })

  const dropOffLabel = intl.formatMessage({
    id: 'tVF7SColq6Au8idGN6rp',
    defaultMessage: 'Drop-off',
    description: 'Label for drop-off date in car hire date range picker',
  })

  return <DropdownSheet
    data-testid="car-hire-date-picker-dropdown"
    size="fill-anchor"
    open={showDropdown}
    anchorRef={anchorRef}
    triggerRef={triggerRef}
    bodyRef={bodyRef}
    primaryActionProps={{
      'data-testid': 'search-apply-button',
      children: applyLabel,
      disabled: disableSearchButton,
      onClick: toggleDropdown,
    }}
    secondaryActionProps={{
      'data-testid': 'search-nodates-button',
      kind: 'tertiary',
      children: cancelLabel,
      onClick: clearDates,
    }}
    footerStart={<Group direction="horizontal" horizontalAlign="end">
      <FormattedSearchDate includeLastDayOnCount timeUnit="day" />
    </Group>}
    onClose={toggleDropdown}
  >
    {isMobileScreen && (
      <VerticalDateRangePicker
        onDatesChange={onDatesChange}
        startDate={pickUpDate}
        endDate={returnDate}
        min={minDate}
        max={maxDate}
        contentRef={bodyRef}
        allowSameDayRange
      />
    )}
    {!isMobileScreen && (
      <DualDateRangePicker
        onDatesChange={onDatesChange}
        startDate={pickUpDate}
        endDate={returnDate}
        minDate={minDate}
        maxDate={maxDate}
        startLabel={pickUpLabel}
        endLabel={dropOffLabel}
        allowSameDayRange
      />
    )}
  </DropdownSheet>
}

export default CarHireDatePickerDropdown
